/**
 * IndexedDB persistence layer.
 *
 * One DB (`ry-customizer`) with two object stores:
 *   - `current`  -> single record at key 'current' holding the live state
 *   - `history`  -> auto-keyed snapshots, FIFO capped at HISTORY_CAP
 *
 * All reads pass through migrate() so old saved states forward-upgrade.
 */
import { type DesignSystem, defaultDesignSystem, migrate } from './state.js';

const DB_NAME = 'ry-customizer';
const DB_VERSION = 1;
const STORE_CURRENT = 'current';
const STORE_HISTORY = 'history';
const CURRENT_KEY = 'current';

export const HISTORY_CAP = 50;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CURRENT)) {
        db.createObjectStore(STORE_CURRENT);
      }
      if (!db.objectStoreNames.contains(STORE_HISTORY)) {
        db.createObjectStore(STORE_HISTORY, { autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | Promise<T>): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const s = transaction.objectStore(store);
        const result = fn(s);
        if (result instanceof IDBRequest) {
          result.onsuccess = () => resolve(result.result);
          result.onerror = () => reject(result.error);
        } else {
          result.then(resolve, reject);
        }
        transaction.onerror = () => reject(transaction.error);
      })
  );
}

/**
 * Load current state. Returns defaults if nothing stored or the stored
 * state is from an unknown schema version we can't migrate.
 */
export async function loadCurrent(): Promise<DesignSystem> {
  try {
    const raw = await tx(STORE_CURRENT, 'readonly', (s) => s.get(CURRENT_KEY));
    return raw ? migrate(raw) : defaultDesignSystem();
  } catch {
    return defaultDesignSystem();
  }
}

/**
 * Save current state. Always stamps a fresh updatedAt.
 */
export async function saveCurrent(ds: DesignSystem): Promise<void> {
  const stamped: DesignSystem = { ...ds, updatedAt: new Date().toISOString() };
  await tx(STORE_CURRENT, 'readwrite', (s) => s.put(stamped, CURRENT_KEY));
}

/**
 * Push a snapshot into history. Evicts oldest entries past HISTORY_CAP.
 */
export async function pushHistory(ds: DesignSystem): Promise<void> {
  await tx(STORE_HISTORY, 'readwrite', async (s) => {
    return new Promise<void>((resolve, reject) => {
      const addReq = s.add({ ...ds });
      addReq.onerror = () => reject(addReq.error);
      addReq.onsuccess = () => {
        // Count and trim
        const countReq = s.count();
        countReq.onerror = () => reject(countReq.error);
        countReq.onsuccess = () => {
          const overflow = countReq.result - HISTORY_CAP;
          if (overflow <= 0) {
            resolve();
            return;
          }
          // Delete oldest N
          const cur = s.openCursor();
          let removed = 0;
          cur.onerror = () => reject(cur.error);
          cur.onsuccess = () => {
            const c = cur.result;
            if (!c || removed >= overflow) {
              resolve();
              return;
            }
            c.delete();
            removed++;
            c.continue();
          };
        };
      };
    });
  });
}

/**
 * List history newest-first. Each entry includes the IDB key for restore.
 */
export async function listHistory(): Promise<Array<{ key: number; state: DesignSystem }>> {
  return tx(STORE_HISTORY, 'readonly', async (s) => {
    return new Promise<Array<{ key: number; state: DesignSystem }>>((resolve, reject) => {
      const items: Array<{ key: number; state: DesignSystem }> = [];
      const cur = s.openCursor(null, 'prev');
      cur.onerror = () => reject(cur.error);
      cur.onsuccess = () => {
        const c = cur.result;
        if (!c) {
          resolve(items);
          return;
        }
        items.push({ key: c.key as number, state: migrate(c.value) });
        c.continue();
      };
    });
  });
}

/**
 * Debounce helper for autosave.
 */
export function debounce<F extends (...args: any[]) => void>(fn: F, ms: number): F {
  let t: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<F>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  }) as F;
}
